import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import Switcher from "../components/switcher";
import { useToast } from "../context/ToastProvider";

const formatDate = (date) => {
    if(!date) return '';
    return new Date(date).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
}

const formatPrice = (amount, currency = 'CLP') => {
    const options = {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'CLP' ? 0 : 2,
        maximumFractionDigits: currency === 'CLP' ? 0 : 2,
    };
    return amount.toLocaleString(currency === 'CLP' ? 'es-CL' : 'en-US', options);
}

export default function Cart(){
    const { state } = useLocation();
    const hasData = Boolean(state && state.total);
    const navigate = useNavigate();
    const toast = useToast();
    // El % de anticipo lo define el backend (hold/departure); no se asume en el front.
    // TODO confirmar con negocio: default 50% vs 100%
    const depositPercent = state?.depositPercent;
    const depositAmount = state?.deposit ?? (state?.total && depositPercent != null ? state.total * (depositPercent / 100) : 0);
    const isHoldExpired = state?.holdExpiresAt ? new Date(state.holdExpiresAt).getTime() < Date.now() : false;

    return (
        <>
            <Navbar navclass="defaultscroll is-sticky" navlight={true} manuclass="justify-end nav-light"/>
            <section className="relative table w-full items-center py-24 bg-[url('../images/bg/cta.jpg')] bg-top bg-no-repeat bg-cover">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"></div>
                <div className="container relative">
                    <div className="grid grid-cols-1 pb-8 text-center mt-10">
                        <h3 className="text-3xl leading-normal tracking-wider font-semibold text-white">Tu carrito</h3>
                        <p className="text-white/70 mt-2">Revisa la reserva antes de confirmar.</p>
                    </div>
                </div>
            </section>

            <section className="relative md:py-16 py-12">
                <div className="container relative">
                    {!hasData && (
                        <div className="text-center bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-8">
                            <p className="text-lg font-semibold mb-3">Aún no tienes reservas.</p>
                            <Link to="/grid" className="text-red-500 font-semibold hover:underline">Explorar tours</Link>
                        </div>
                    )}

                    {hasData && (
                        <div className="grid md:grid-cols-3 grid-cols-1 gap-6">
                            <div className="md:col-span-2 bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-6">
                                <h5 className="text-xl font-semibold mb-4">Detalle de la reserva</h5>
                                <div className="space-y-2 text-sm">
                                    {state.destination && (
                                        <p><span className="font-semibold">Destino:</span> {state.destination}</p>
                                    )}
                                    <p><span className="font-semibold">Fechas:</span> {formatDate(state.startDate)} - {formatDate(state.endDate)}</p>
                                    <p><span className="font-semibold">Personas:</span> {state.adults} adultos {state.children > 0 ? `, ${state.children} niños` : ''}</p>
                                    <p><span className="font-semibold">Extras:</span> {state.extras && state.extras.length ? state.extras.join(', ') : 'Sin extras'}</p>
                                    {state.title && <p><span className="font-semibold">Tour:</span> {state.title}</p>}
                                    {state.holdExpiresAt && (
                                        <p className={`text-sm ${isHoldExpired ? 'text-red-500' : 'text-slate-500'}`}>
                                            Cupo bloqueado hasta {new Date(state.holdExpiresAt).toLocaleTimeString('es-CL')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-6">
                                <h5 className="text-xl font-semibold mb-4">Resumen</h5>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Total</span>
                                        <span className="font-semibold">{formatPrice(state.total, state.currency)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Anticipo ({depositPercent}%)</span>
                                        <span className="font-semibold text-red-500">{formatPrice(depositAmount, state.currency)}</span>
                                    </div>
                                </div>
                                <div className="mt-4 space-y-2">
                                    <button
                                        type="button"
                                        onClick={()=>{
                                            if(isHoldExpired){
                                                toast.error('El cupo expiró, selecciona nuevamente.');
                                                return;
                                            }
                                            navigate('/checkout', { state: { ...state, deposit: depositAmount, depositPercent } });
                                        }}
                                        className={`w-full py-2 px-4 rounded-md ${hasData ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                                        disabled={!hasData}
                                    >
                                        Ir a pagar
                                    </button>
                                    <Link to="/grid" className="block text-center text-red-500 font-semibold hover:underline">Seguir explorando</Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <Footer/>
            <Switcher/>
        </>
    )
}
